import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, X } from "lucide-react";

import { useTranslation } from "@/i18n/use-translation";

type Props = {
  /** The resolved registry title — used in the control aria-labels. */
  title: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  children: ReactNode;
};

const CONTROL =
  "rounded-full p-1.5 text-gray-400 transition-colors duration-150 " +
  "hover:bg-blue-50 hover:text-blue-600 focus-visible:ring-1 focus-visible:ring-blue-500 " +
  "outline-none disabled:pointer-events-none disabled:opacity-40";

/**
 * Tail widget wrapper (POS2-877): the mock's hover pill (↑ ↓ ✕) on the top
 * border. Revealed on hover/keyboard focus (opacity, not display — hidden
 * buttons would be unreachable by keyboard) and always visible where hover
 * doesn't exist (touch). The fixed core never renders inside this frame.
 */
export function WidgetFrame({
  title,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRemove,
  children,
}: Props) {
  const { t } = useTranslation();
  return (
    <div className="group relative" data-testid="widget-frame">
      <div
        className={
          "pointer-events-none absolute -top-3 right-4 z-10 flex items-center gap-0.5 " +
          "rounded-full border border-gray-200/80 bg-white p-0.5 shadow-sm " +
          "opacity-0 transition-opacity duration-150 " +
          "group-hover:pointer-events-auto group-hover:opacity-100 " +
          "focus-within:pointer-events-auto focus-within:opacity-100 " +
          "[@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100"
        }
      >
        <button
          type="button"
          className={CONTROL}
          aria-label={t("dashboard.moveWidgetUp", { widget: title })}
          disabled={!canMoveUp}
          onClick={onMoveUp}
        >
          <ArrowUp className="size-3.5" aria-hidden />
        </button>
        <button
          type="button"
          className={CONTROL}
          aria-label={t("dashboard.moveWidgetDown", { widget: title })}
          disabled={!canMoveDown}
          onClick={onMoveDown}
        >
          <ArrowDown className="size-3.5" aria-hidden />
        </button>
        <button
          type="button"
          className={`${CONTROL} hover:bg-red-50 hover:text-red-600`}
          aria-label={t("dashboard.removeWidget", { widget: title })}
          onClick={onRemove}
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </div>
      {children}
    </div>
  );
}
